import { useState } from "react";
import { Navigate } from "react-router-dom";
import { ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { getDeviceId, getDeviceInfo } from "../lib/device";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");
  const [captcha, setCaptcha] = useState(null);
  const [captchaText, setCaptchaText] = useState("");
  const expiredByIdle = new URLSearchParams(window.location.search).get("motivo") === "inatividade";

  if (user) return <Navigate to="/" replace />;

  const loadCaptcha = async () => {
    try {
      const { data } = await api.get("/auth/captcha");
      setCaptcha(data);
      setCaptchaText("");
    } catch {
      setCaptcha(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        ...form, device_id: getDeviceId(), device_info: getDeviceInfo(),
        captcha_id: captcha?.captcha_id || "", captcha_text: captchaText,
      });
      if (data.requires_2fa) {
        setTwofa({ token: data.temp_token });
      } else {
        if (data.emergency_access) {
          toast.warning("Acesso administrativo de emergência ativo — todas as ações desta sessão serão auditadas.");
        }
        setUser(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (detail === "CAPTCHA_REQUIRED") {
        setError("Por segurança, digite o código de verificação da imagem abaixo.");
      } else {
        setError(apiError(err));
      }
      if (status === 401 || detail === "CAPTCHA_REQUIRED") {
        loadCaptcha();
      } else if (status === 429) {
        setCaptcha(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/verify", { temp_token: twofa.token, code });
      if (data.backup_code_used) {
        toast.warning(`Código de backup utilizado — restam ${data.backup_codes_remaining}. Gere novos no Perfil se necessário.`);
      }
      setUser(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2.5 text-sm text-zinc-50 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md fade-up">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <ChartLineUp size={28} weight="bold" className="text-indigo-500" />
          <span className="font-display font-semibold text-2xl tracking-tight text-zinc-50">FluxoCRM</span>
        </div>
        <div className="border border-zinc-800 bg-zinc-900/40 p-8 rounded-lg">
          <h1 className="font-display text-xl font-semibold text-zinc-50 mb-1">
            {twofa ? "Verificação em duas etapas" : "Entrar no sistema"}
          </h1>
          <p className="text-sm text-zinc-500 mb-6">
            {twofa ? "Digite o código de 6 dígitos do seu app autenticador" : "Gestão de clientes, pedidos e fluxo financeiro"}
          </p>
          {twofa ? (
            <form onSubmit={verify2fa} className="space-y-4">
              <input
                data-testid="twofa-code-input"
                className={`${input} text-center tracking-[0.4em] text-lg`}
                placeholder="000000"
                maxLength={9}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
              <p className="text-xs text-zinc-500">Também aceita um código de backup (formato XXXX-XXXX).</p>
              {error && (
                <p data-testid="login-error" className="text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded px-3 py-2">{error}</p>
              )}
              <button
                data-testid="twofa-verify-button"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded transition-colors"
              >
                {loading ? "Verificando..." : "Verificar"}
              </button>
              <button type="button" onClick={() => { setTwofa(null); setCode(""); setError(""); }}
                className="w-full text-sm text-zinc-400 hover:text-indigo-400 transition-colors">
                Voltar ao login
              </button>
            </form>
          ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="login-email-input"
              type="email"
              className={input}
              placeholder="E-mail"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <input
              data-testid="login-password-input"
              type="password"
              className={input}
              placeholder="Senha"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
            />
            {captcha && (
              <div data-testid="login-captcha-block" className="space-y-2 border border-zinc-800 rounded p-3 bg-zinc-900/60">
                <div className="flex items-center gap-3">
                  <img src={captcha.image} alt="Código de verificação" className="rounded h-14 bg-white" data-testid="login-captcha-image" />
                  <button type="button" data-testid="login-captcha-refresh" onClick={loadCaptcha}
                    className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors">
                    Gerar outro código
                  </button>
                </div>
                <input
                  data-testid="login-captcha-input"
                  className={`${input} uppercase tracking-[0.3em]`}
                  placeholder="Digite o código da imagem"
                  maxLength={5}
                  value={captchaText}
                  onChange={(e) => setCaptchaText(e.target.value.toUpperCase())}
                  required
                />
              </div>
            )}
            {error && (
              <p data-testid="login-error" className="text-sm text-rose-400 bg-rose-400/10 border border-rose-400/20 rounded px-3 py-2">{error}</p>
            )}
            {!error && expiredByIdle && (
              <p data-testid="login-idle-notice" className="text-sm text-amber-300 bg-amber-400/10 border border-amber-400/20 rounded px-3 py-2">
                Sua sessão foi encerrada por inatividade. Entre novamente para continuar.
              </p>
            )}
            <button
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white font-medium py-2.5 rounded transition-colors"
            >
              {loading ? "Aguarde..." : "Entrar"}
            </button>
            <p className="text-xs text-zinc-600 text-center">
              Novas contas são criadas apenas pelo administrador do sistema.
            </p>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
